/**
 * Webhook Service
 * HTTP service for webhook CRUD operations and delivery logs
 */
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '@environments/environment';
import {
  Webhook,
  WebhookDelivery,
  CreateWebhookRequest,
  UpdateWebhookRequest,
  TestWebhookRequest,
  CreateWebhookResponse,
  ListWebhooksResponse,
  ListWebhookDeliveriesResponse,
  TestWebhookResponse,
  WebhookDeliveryStatus
} from '../models/webhook.models';

@Injectable({
  providedIn: 'root'
})
export class WebhookService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/webhooks`;

  /**
   * Create a new webhook
   */
  createWebhook(request: CreateWebhookRequest): Observable<CreateWebhookResponse> {
    return this.http.post<CreateWebhookResponse>(this.apiUrl, request).pipe(
      map(response => ({
        ...response,
        webhook: this.normalizeWebhook(response.webhook)
      }))
    );
  }

  /**
   * Get all webhooks for the organization
   */
  getWebhooks(
    page: number = 1,
    pageSize: number = 20,
    isActive?: boolean
  ): Observable<ListWebhooksResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('page_size', pageSize.toString());

    if (isActive !== undefined) {
      params = params.set('is_active', isActive.toString());
    }

    return this.http.get<ListWebhooksResponse>(this.apiUrl, { params }).pipe(
      map(response => ({
        ...response,
        webhooks: (response.webhooks || []).map(webhook => this.normalizeWebhook(webhook))
      }))
    );
  }

  /**
   * Get a specific webhook by ID
   */
  getWebhook(webhookId: string): Observable<Webhook> {
    return this.http.get<Webhook>(`${this.apiUrl}/${webhookId}`).pipe(
      map(webhook => this.normalizeWebhook(webhook))
    );
  }

  /**
   * Update a webhook
   */
  updateWebhook(webhookId: string, request: UpdateWebhookRequest): Observable<Webhook> {
    return this.http.put<Webhook>(`${this.apiUrl}/${webhookId}`, request).pipe(
      map(webhook => this.normalizeWebhook(webhook))
    );
  }

  /**
   * Delete a webhook
   */
  deleteWebhook(webhookId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${webhookId}`);
  }

  /**
   * Test a webhook
   */
  testWebhook(webhookId: string, request: TestWebhookRequest): Observable<TestWebhookResponse> {
    return this.http.post<TestWebhookResponse>(`${this.apiUrl}/${webhookId}/test`, request);
  }

  /**
   * Get delivery logs for a webhook
   */
  getWebhookDeliveries(
    webhookId: string,
    page: number = 1,
    pageSize: number = 20,
    status?: WebhookDeliveryStatus
  ): Observable<ListWebhookDeliveriesResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('page_size', pageSize.toString());

    if (status) {
      params = params.set('status', status);
    }

    return this.http.get<ListWebhookDeliveriesResponse>(
      `${this.apiUrl}/${webhookId}/deliveries`,
      { params }
    );
  }

  /**
   * Helper: Check if webhook is healthy (success rate > 90%)
   */
  private normalizeWebhook(webhook: Webhook & { id?: string }): Webhook {
    return {
      ...webhook,
      _id: webhook._id || webhook.id || ''
    };
  }

  isWebhookHealthy(webhook: Webhook): boolean {
    const stats = webhook.statistics;
    if (stats.total_deliveries === 0) return true;
    const successRate = (stats.successful_deliveries / stats.total_deliveries) * 100;
    return successRate >= 90;
  }

  /**
   * Helper: Get webhook health status
   */
  getWebhookHealth(webhook: Webhook): 'healthy' | 'warning' | 'critical' {
    const stats = webhook.statistics;
    if (stats.total_deliveries === 0) return 'healthy';
    
    const successRate = (stats.successful_deliveries / stats.total_deliveries) * 100;
    
    if (successRate >= 90) return 'healthy';
    if (successRate >= 70) return 'warning';
    return 'critical';
  }

  /**
   * Helper: Format delivery time
   */
  formatDeliveryTime(delivery: WebhookDelivery): string {
    if (delivery.delivered_at) {
      const deliveryTime = new Date(delivery.delivered_at).getTime() - new Date(delivery.created_at).getTime();
      return `${Math.round(deliveryTime / 1000)}s`;
    }
    return 'N/A';
  }

  /**
   * Helper: Get next retry time
   */
  getNextRetryTime(delivery: WebhookDelivery): string {
    if (!delivery.next_retry_at) return 'N/A';
    
    const now = new Date().getTime();
    const nextRetry = new Date(delivery.next_retry_at).getTime();
    const diff = nextRetry - now;
    
    if (diff <= 0) return 'Retrying now...';
    
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `in ${minutes}m`;
    
    const hours = Math.floor(minutes / 60);
    return `in ${hours}h`;
  }
}

